import { CompetitionData } from "../../../components/ActiveCompetitions/types";
import { createAppSelector, useAppSelector } from "../../hooks";
import { CompetitionCategory } from "./slice";

const createCompetitionCategoriesSelector = createAppSelector(
  [
    (state, options?: { excludeCategories?: string[] }) => state.competitionsCategories,
    (state, options?: { excludeCategories?: string[] }) => options,
  ],
  (competitionCategories, options) => {
    const includedCategories = options?.excludeCategories
      ? competitionCategories.categories.filter(category => !options?.excludeCategories.includes(category.type))
      : competitionCategories.categories;
    return includedCategories.map(category => ({
      ...category,
      competitions: category.competitionIds.map(competitionId => {
        const comp = competitionCategories.activeCompetitions.find(
          activeCompetition => activeCompetition.CompetitionId === competitionId
        );
        if (comp) {
          return comp;
        }
        return {
          AdditionalCashText: "",
          CompetitionId: 0,
          CompetitionPictureTypeId: 0,
          CtaAddToBasketPrizeId: 0,
          CtaText: "",
          CtaTextValidated: "",
          CtaTicketValue: "",
          CurrencySymbol: "",
          EndDate: "",
          FormattedOriginalPrice: "",
          FormattedPrice: "",
          FormattedPriceWithoutCurrency: "",
          HasInstantWin: false,
          ImageDoubleUrl: "",
          ImageUrl: "",
          IsRewardCompetition: false,
          IsSinglePrizeMode: false,
          MultiBuyLowerPercent: 0,
          MultiBuyLowerQuantity: 0,
          MultiBuyUpperPercent: 0,
          MultiBuyUpperQuantity: 0,
          NavigationColour: "",
          OverrideDiscountValue: 0,
          PageUrl: "",
          PercentOffValue: 0,
          PostalEntryText: "",
          SupportsMultibuy: false,
          TicketText: "",
          Title: "",
          UseDailyPopupForCompetitionId: 0,
        } as CompetitionData;
      }),
    }));
  }
);
export const useCompetitionCategories = (options?: { excludeCategories?: string[] }) =>
  useAppSelector(state => createCompetitionCategoriesSelector(state, options));

const createCompetitionByTypeSelector = createAppSelector(
  [
    (state, type: CompetitionCategory["type"]) => state.competitionsCategories,
    (state, type: CompetitionCategory["type"]) => type,
  ],
  (competitionCategories, type) => {
    const category = competitionCategories.categories.find(category => category.type === type);
    if (category) {
      return {
        ...category,
        competitions: category.competitionIds.map(competitionId => {
          const comp = competitionCategories.activeCompetitions.find(
            activeCompetition => activeCompetition.CompetitionId === competitionId
          );
          if (comp) {
            return comp;
          }
          return {
            AdditionalCashText: "",
            CompetitionId: 0,
            CompetitionPictureTypeId: 0,
            CtaAddToBasketPrizeId: 0,
            CtaText: "",
            CtaTextValidated: "",
            CtaTicketValue: "",
            CurrencySymbol: "",
            EndDate: "",
            FormattedOriginalPrice: "",
            FormattedPrice: "",
            FormattedPriceWithoutCurrency: "",
            HasInstantWin: false,
            ImageDoubleUrl: "",
            ImageUrl: "",
            IsRewardCompetition: false,
            IsSinglePrizeMode: false,
            MultiBuyLowerPercent: 0,
            MultiBuyLowerQuantity: 0,
            MultiBuyUpperPercent: 0,
            MultiBuyUpperQuantity: 0,
            NavigationColour: "",
            OverrideDiscountValue: 0,
            PageUrl: "",
            PercentOffValue: 0,
            PostalEntryText: "",
            SupportsMultibuy: false,
            TicketText: "",
            Title: "",
            UseDailyPopupForCompetitionId: 0,
          } as CompetitionData;
        }),
      } as CompetitionCategory;
    }
    return {
      competitions: [],
      type,
      imageUrl: "",
      subtitle: "",
      title: "",
    };
  }
);
export const useCompetitionByType = (type: CompetitionCategory["type"]) =>
  useAppSelector(store => createCompetitionByTypeSelector(store, type));

export const useCompetitionsCategoriesLoading = () => useAppSelector(store => store.competitionsCategories.loading);

export const useCategoriesFeaturedImage = () => useAppSelector(store => store.competitionsCategories.featuredImage);

export const useActiveCompetitions = () => useAppSelector(store => store.competitionsCategories.activeCompetitions);

const createCompetitionSelector = createAppSelector(
  [
    (state, options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>) =>
      state.competitionsCategories.activeCompetitions,
    (state, options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>) => options,
  ],
  (activeCompetitions, options) => {
    return (
      activeCompetitions.find(
        comp =>
          comp.CompetitionId === options?.CompetitionId || comp.CompetitionTypePrefix === options?.CompetitionTypePrefix
      ) ||
      ({
        AdditionalCashText: "",
        CompetitionId: 0,
        CompetitionPictureTypeId: 0,
        CompetitionTypePrefix: "",
        CtaAddToBasketPrizeId: 0,
        CtaText: "",
        CtaTextValidated: "",
        CtaTicketValue: "",
        CurrencySymbol: "",
        EndDate: "",
        FormattedOriginalPrice: "",
        FormattedPrice: "",
        FormattedPriceWithoutCurrency: "",
        HasInstantWin: false,
        ImageDoubleUrl: "",
        ImageUrl: "",
        IsMobileAppOnly: false,
        IsMobileOnlyCompetition: false,
        IsMultibuyEnabled: false,
        IsRewardCompetition: false,
        IsSinglePrizeMode: false,
        MultiBuyLowerPercent: 0,
        MultiBuyLowerQuantity: 0,
        MultiBuyUpperPercent: 0,
        MultiBuyUpperQuantity: 0,
        NavigationColour: "",
        OverrideCompetitionTypeId: undefined,
        OverrideDiscountValue: 0,
        PageUrl: "",
        PercentOffValue: 0,
        PostalEntryText: "",
        SubtitleText: "",
        SubtitleTextValidated: "",
        SupportsMultibuy: false,
        TicketText: "",
        Title: "",
        UseDailyPopupForCompetitionId: 0,
      } as CompetitionData)
    );
  }
);
export const useCompetition = (options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>) =>
  useAppSelector(store => createCompetitionSelector(store, options));

const createCompetitionsSelector = createAppSelector(
  [
    (state, options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>[]) =>
      state.competitionsCategories.activeCompetitions,
    (state, options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>[]) => options,
  ],
  (activeCompetitions, options) => {
    if (!options || options.length === 0) return [];
    return activeCompetitions.filter(
      comp =>
        options.findIndex(
          option =>
            comp.CompetitionId === option?.CompetitionId || comp.CompetitionTypePrefix === option?.CompetitionTypePrefix
        ) !== -1
    );
  }
);
export const useCompetitions = (
  options?: Partial<Pick<CompetitionData, "CompetitionId" | "CompetitionTypePrefix">>[]
) => useAppSelector(store => createCompetitionsSelector(store, options));
